import { Component, Input, OnInit, ViewChild } from '@angular/core'
import {
  ConfirmDialogService,
  Destroyable,
  isSomething,
  Rental,
  Toaster,
  untilDestroy,
  UploadBoxOptions,
} from '@tokeet-frontend/tv3-platform'
import { environment } from '@tv3/environments/environment'
import { AuthService } from '@tv3/services/auth.service'
import { MatPaginator } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import { FormControl } from '@angular/forms'
import { AiResponderDoc } from '@tv3/containers/ai-responder/store/ai-settings.model'
import { localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { Store } from '@ngrx/store'
import {
  DeleteAIDocument,
  UpdateAISettings,
  UploadAIDocumentComplete,
} from '@tv3/containers/ai-responder/store/ai-settings.actions'
import { SelectRentalDialogService } from '@tv3/shared/dialogs/select-rental-dialog/select-rental-dialog.service'
import { selectAISettingsDocs } from '../../store/ai-settings.selectors'

@Component({
  selector: 'ai-responder-documents',
  templateUrl: './ai-responder-documents.component.html',
  styleUrls: ['./ai-responder-documents.component.scss'],
})
export class AiResponderDocumentsComponent extends Destroyable implements OnInit {
  @ViewChild('paginator') set paginator(p: MatPaginator) {
    this.dataSource.paginator = p
  }
  @ViewChild(MatSort) set sort(s: MatSort) {
    this.dataSource.sort = s
  }

  @Input() rentalId: string

  documents: AiResponderDoc[] = []

  displayedColumns: string[] = ['name', 'bytes', 'preBooking', 'postBooking', 'postCheckin', 'rentals', 'actions']
  dataSource = new MatTableDataSource<AiResponderDoc>()
  searchCtrl = new FormControl()

  uploadBoxOptions: UploadBoxOptions = {
    autoUpload: true,
    resetAfterFailed: true,
    resetAfterCompleted: true,
    maxSize: 1024 * 1024 * 20,
    maxUploads: 1,
    uploadUrl: `${environment.apiUrl}/ai/responder/files`,
    token: this.authService.token,
    allowedFileExtensions: ['.md', '.txt'],
    guide: $localize`You can upload only 1 file at a time that is 20 MB or smaller. Maximum 300 files are allowed to be uploaded in total. only MD and TXT files are allowed`,
  }

  maxFiles = 300

  get isMaxFilesReached() {
    return this.documents.length >= this.maxFiles
  }

  constructor(
    private store: Store<any>,
    private authService: AuthService,
    private toaster: Toaster,
    private confirm: ConfirmDialogService,
    private selectRentalDialog: SelectRentalDialogService
  ) {
    super()
  }

  ngOnInit(): void {
    this.searchCtrl.valueChanges.pipe(untilDestroy(this)).subscribe((term) => {
      this.dataSource.filter = term
    })
    this.dataSource.sortData = localeCompareSort
    this.store
      .select(selectAISettingsDocs)
      .pipe(untilDestroy(this))
      .subscribe((docs) => {
        this.documents = docs
        if (this.rentalId) {
          this.dataSource.data = docs.filter(
            (doc) => !isSomething(doc.linkedRentals) || doc.linkedRentals?.includes(this.rentalId)
          )
        } else {
          this.dataSource.data = docs
        }
      })
  }

  onUploadComplete(data: { doc: AiResponderDoc }[]) {
    data?.forEach(({ doc }) => {
      this.store.dispatch(UploadAIDocumentComplete({ doc }))
      if (this.rentalId) {
        doc.linkedRentals = [this.rentalId]
        this.onDocChanged(doc, true)
      }
    })
    this.toaster.success($localize`File uploaded successfully`)
  }

  onTogglePreBooking(item: AiResponderDoc, checked: boolean) {
    this.onDocChanged({ ...item, preBooking: checked })
  }

  onTogglePostBooking(item: AiResponderDoc, checked: boolean) {
    this.onDocChanged({ ...item, postBooking: checked })
  }

  onTogglePostCheckin(item: AiResponderDoc, checked: boolean) {
    this.onDocChanged({ ...item, postCheckin: checked })
  }

  onDocChanged(item: AiResponderDoc, silent?: boolean) {
    const docs = this.documents.map((t) => (t.id == item.id ? item : t))
    this.store.dispatch(
      UpdateAISettings({
        payload: { docs },
        message: $localize`Data saved successfully`,
        silent,
      })
    )
  }

  onChangeRentals(item: AiResponderDoc) {
    this.selectRentalDialog
      .open({
        title: $localize`Select Rentals`,
        selectedIds: item.linkedRentals,
        multiple: true,
        required: false,
      })
      .subscribe((rentals: Rental[]) => {
        this.onDocChanged({ ...item, linkedRentals: rentals.map((r) => r.id) })
      })
  }

  onUnlinkRental(item: AiResponderDoc) {
    this.confirm
      .confirm({
        title: $localize`Unlink Rental`,
        body: $localize`Are you sure you want to unlink this rental?`,
      })
      .subscribe(() => {
        this.onDocChanged({ ...item, linkedRentals: item.linkedRentals.filter((r) => r !== this.rentalId) })
      })
  }

  onDelete(item: AiResponderDoc) {
    this.confirm.confirm().subscribe(() => {
      this.store.dispatch(DeleteAIDocument({ id: item.id }))
    })
  }
}
